import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ShieldCheck, ShieldAlert, ShieldX } from 'lucide-react';
import {
  getLabourSafety,
  getLabourSafetyOptions,
  saveLabourSafety
} from '../../services/api/implementation/impl';

/**
 * A workman's safety record, and whether they may start.
 *
 * Grouped the way somebody fills it in rather than the way it is stored:
 * Emergency & Medical first because it is what an ambulance needs, then Work
 * Fitness, then Site Safety. Secondary fields stay hidden until the answer
 * above them makes them relevant — showing every one upfront turns a ten-field
 * section into a thirty-field one, most of it not applicable.
 *
 * The verdict at the top comes from the backend, not from re-deciding it here.
 * The same three lights have to appear on the app and in a report, and a second
 * implementation in JavaScript would drift from the one that actually decides.
 */

const REQUIRED = ' *';

const STATUS_LOOK = {
  SITE_READY: {
    label: 'Site ready',
    cls: 'border-success-border bg-success-bg text-success-fg',
    Icon: ShieldCheck
  },
  ACTION_REQUIRED: {
    label: 'Action required',
    cls: 'border-warning-border bg-warning-bg text-warning-fg',
    Icon: ShieldAlert
  },
  NOT_CLEARED: {
    label: 'Not cleared',
    cls: 'border-danger-border bg-danger-bg text-danger-fg',
    Icon: ShieldX
  }
};

const LabourSafetySection = ({ labourId }) => {
  const queryClient = useQueryClient();

  const options = useQuery({
    queryKey: ['labourSafetyOptions'],
    queryFn: getLabourSafetyOptions,
    staleTime: 60 * 60 * 1000
  });

  const record = useQuery({
    queryKey: ['labourSafety', labourId],
    queryFn: () => getLabourSafety(labourId),
    enabled: Boolean(labourId)
  });

  const [form, setForm] = useState(null);

  // Seeded once the record arrives, and again if the workman changes.
  useEffect(() => {
    if (!record.data) return;
    const s = record.data.safety ?? {};
    setForm({
      blood_group: s.blood_group ?? '',
      emergency_contact_name: s.emergency_contact_name ?? '',
      emergency_contact_mobile: s.emergency_contact_mobile ?? '',
      emergency_contact_relationship: s.emergency_contact_relationship ?? '',
      allergies: s.allergies ?? [],
      allergy_other: s.allergy_other ?? '',
      medical_conditions: s.medical_conditions ?? [],
      medical_condition_other: s.medical_condition_other ?? '',
      emergency_instructions: s.emergency_instructions ?? '',
      fit_to_work: s.fit_to_work ?? 'NOT_ASSESSED',
      work_restrictions: s.work_restrictions ?? [],
      medical_certificate_url: s.medical_certificate_url ?? '',
      medical_certificate_on: dayValue(s.medical_certificate_on),
      medical_certificate_valid_until: dayValue(s.medical_certificate_valid_until),
      ppe_status: s.ppe_status ?? '',
      ppe_issued: s.ppe_issued ?? [],
      safety_induction: s.safety_induction ?? '',
      induction_on: dayValue(s.induction_on),
      induction_by: s.induction_by ?? '',
      height_training: s.height_training ?? '',
      height_training_on: dayValue(s.height_training_on),
      height_training_valid_until: dayValue(s.height_training_valid_until),
      height_training_certificate_url: s.height_training_certificate_url ?? ''
    });
  }, [record.data]);

  const save = useMutation({
    mutationFn: (payload) => saveLabourSafety(labourId, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(['labourSafety', labourId], data);
      toast.success('Safety details saved');
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not save the safety details')
  });

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const toggle = (key) => (value) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(value)
        ? f[key].filter((v) => v !== value)
        : [...f[key], value]
    }));

  const opts = options.data ?? {};
  const verdict = record.data;

  // Which secondary fields the answers above have made relevant.
  const show = useMemo(
    () => ({
      restrictions: form?.fit_to_work === 'FIT_WITH_RESTRICTIONS',
      ppeChecklist:
        form?.ppe_status === 'PARTIALLY_ISSUED' || form?.ppe_status === 'COMPLETE',
      inductionDetail: form?.safety_induction === 'COMPLETED',
      heightDetail: form?.height_training === 'TRAINED',
      allergyOther: (form?.allergies ?? []).includes('Other'),
      conditionOther: (form?.medical_conditions ?? []).includes('Other')
    }),
    [form]
  );

  if (record.isLoading || !form) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-500">
        Loading safety details…
      </div>
    );
  }

  const look = STATUS_LOOK[verdict?.status] ?? STATUS_LOOK.NOT_CLEARED;
  const StatusIcon = look.Icon;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm font-semibold text-gray-800">Safety details</div>
        <div
          className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium ${look.cls}`}
        >
          <StatusIcon size={16} />
          {look.label}
        </div>
      </div>

      {/* Why, not just what colour. A light with no reason beside it is what
          makes site staff ring the office. */}
      {verdict?.reasons?.length ? (
        <ul className="space-y-1 text-sm text-gray-700">
          {verdict.reasons.map((reason) => (
            <li key={reason} className="flex gap-2">
              <span aria-hidden="true">•</span>
              <span>{reason}</span>
            </li>
          ))}
        </ul>
      ) : null}

      <Group title="Emergency &amp; medical" hint="What an ambulance would need to know.">
        <Row>
          <Select
            label={`Blood group${REQUIRED}`}
            value={form.blood_group}
            onChange={set('blood_group')}
            options={opts.blood_group ?? []}
          />
          {/* Both halves are required for Site ready. The name box was labelled
              just "Emergency contact" and Mobile carried no star, so a number
              went in the name box, Mobile stayed empty, and the status sat on
              Action required with no obvious reason. */}
          <Text
            label={`Emergency contact name${REQUIRED}`}
            value={form.emergency_contact_name}
            onChange={set('emergency_contact_name')}
            placeholder="Who to call"
          />
          <Text
            label={`Emergency contact mobile${REQUIRED}`}
            value={form.emergency_contact_mobile}
            onChange={(value) =>
              set('emergency_contact_mobile')(
                String(value || '').replace(/\D/g, '').slice(0, 10)
              )
            }
            placeholder="10 digits"
            inputMode="numeric"
          />
          <Select
            label="Relationship"
            value={form.emergency_contact_relationship}
            onChange={set('emergency_contact_relationship')}
            options={opts.emergency_relationship ?? []}
          />
        </Row>

        <Chips
          label="Allergies"
          values={form.allergies}
          options={opts.allergies ?? []}
          onToggle={toggle('allergies')}
        />
        {show.allergyOther ? (
          <Text
            label="Which allergy"
            value={form.allergy_other}
            onChange={set('allergy_other')}
          />
        ) : null}

        <Chips
          label="Medical conditions"
          values={form.medical_conditions}
          options={opts.medical_conditions ?? []}
          onToggle={toggle('medical_conditions')}
        />
        {show.conditionOther ? (
          <Text
            label="Which condition"
            value={form.medical_condition_other}
            onChange={set('medical_condition_other')}
          />
        ) : null}

        <Area
          label="Emergency instructions"
          hint="Medication, precautions, who to call first."
          value={form.emergency_instructions}
          onChange={set('emergency_instructions')}
        />
      </Group>

      <Group title="Work fitness">
        <Row>
          <Select
            label={`Fit to work${REQUIRED}`}
            value={form.fit_to_work}
            onChange={set('fit_to_work')}
            options={opts.fit_to_work ?? []}
          />
          <Text
            label="Medical certificate"
            value={form.medical_certificate_url}
            onChange={set('medical_certificate_url')}
            placeholder="Link, if you have one"
          />
          <Day
            label="Certificate date"
            value={form.medical_certificate_on}
            onChange={set('medical_certificate_on')}
          />
          <Day
            label="Valid until"
            value={form.medical_certificate_valid_until}
            onChange={set('medical_certificate_valid_until')}
          />
        </Row>
        {show.restrictions ? (
          <Chips
            label="What they must not do"
            values={form.work_restrictions}
            options={opts.work_restrictions ?? []}
            onToggle={toggle('work_restrictions')}
          />
        ) : null}
      </Group>

      <Group title="Site safety">
        <Row>
          <Select
            label={`PPE${REQUIRED}`}
            value={form.ppe_status}
            onChange={set('ppe_status')}
            options={opts.ppe_status ?? []}
          />
          <Select
            label={`Safety induction${REQUIRED}`}
            value={form.safety_induction}
            onChange={set('safety_induction')}
            options={opts.safety_induction ?? []}
          />
          <Select
            label="Work at height"
            value={form.height_training}
            onChange={set('height_training')}
            options={opts.height_training ?? []}
          />
        </Row>

        {show.ppeChecklist ? (
          <Chips
            label="Issued"
            values={form.ppe_issued}
            options={opts.ppe_items ?? []}
            onToggle={toggle('ppe_issued')}
          />
        ) : null}

        {show.inductionDetail ? (
          <Row>
            <Day
              label="Induction date"
              value={form.induction_on}
              onChange={set('induction_on')}
            />
            <Text
              label="Conducted by"
              value={form.induction_by}
              onChange={set('induction_by')}
            />
          </Row>
        ) : null}

        {show.heightDetail ? (
          <Row>
            <Day
              label="Training date"
              value={form.height_training_on}
              onChange={set('height_training_on')}
            />
            <Day
              label="Valid until"
              value={form.height_training_valid_until}
              onChange={set('height_training_valid_until')}
            />
            <Text
              label="Certificate"
              value={form.height_training_certificate_url}
              onChange={set('height_training_certificate_url')}
              placeholder="Link, if you have one"
            />
          </Row>
        ) : null}
      </Group>

      <div className="flex justify-end">
        <button
          type="button"
          disabled={save.isPending}
          onClick={() => {
            // A number in the name box is the mistake the old labels invited.
            if (/^\+?[\d\s-]{7,}$/.test(String(form.emergency_contact_name || '').trim())) {
              toast.error(
                "That looks like a phone number. Put it under Emergency contact mobile, and the person's name in the name box."
              );
              return;
            }
            const mobile = String(form.emergency_contact_mobile || '');
            if (mobile && mobile.length !== 10) {
              toast.error('Emergency contact mobile must be 10 digits');
              return;
            }
            save.mutate(form);
          }}
          className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {save.isPending ? 'Saving…' : 'Save safety details'}
        </button>
      </div>
    </div>
  );
};

/* --- small pieces --------------------------------------------------------- */

/** A `date` column arrives as YYYY-MM-DD or an ISO string; the input wants the former. */
const dayValue = (raw) => (raw ? String(raw).slice(0, 10) : '');

const humanise = (value) =>
  String(value ?? '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());

const Group = ({ title, hint, children }) => (
  <section className="space-y-3">
    <div>
      <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
      {hint ? <p className="text-xs text-gray-500">{hint}</p> : null}
    </div>
    {children}
  </section>
);

const Row = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-3">
    {children}
  </div>
);

const Label = ({ children }) => (
  <span className="block text-xs font-medium text-gray-600 mb-1">{children}</span>
);

const field =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-600 focus:outline-none';

const Text = ({ label, value, onChange, placeholder, inputMode }) => (
  <label className="block">
    <Label>{label}</Label>
    <input
      className={field}
      value={value}
      inputMode={inputMode}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  </label>
);

const Day = ({ label, value, onChange }) => (
  <label className="block">
    <Label>{label}</Label>
    <input
      type="date"
      className={field}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </label>
);

const Select = ({ label, value, onChange, options }) => (
  <label className="block">
    <Label>{label}</Label>
    <select
      className={`${field} bg-white`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">—</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {humanise(o)}
        </option>
      ))}
    </select>
  </label>
);

/**
 * Multi-select as tappable chips rather than a multiple-select box.
 *
 * This is filled in on a phone at a site gate as often as at a desk, and a
 * native multi-select needs a keyboard modifier to pick a second value.
 */
const Chips = ({ label, values, options, onToggle }) => (
  <div>
    <Label>{label}</Label>
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const on = values.includes(option);
        return (
          <button
            key={option}
            type="button"
            aria-pressed={on}
            onClick={() => onToggle(option)}
            className={`rounded-lg border px-3 py-1.5 text-sm ${
              on
                ? 'border-brand-700 bg-brand-50 text-brand-900'
                : 'border-gray-200 text-gray-700'
            }`}
          >
            {option}
          </button>
        );
      })}
    </div>
  </div>
);

const Area = ({ label, hint, value, onChange }) => (
  <label className="block">
    <Label>{label}</Label>
    <textarea
      rows={3}
      className={field}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
    {hint ? <p className="mt-1 text-xs text-gray-500">{hint}</p> : null}
  </label>
);

export default LabourSafetySection;
